import XLSX from 'xlsx';
import moment from 'moment';
import { orderBy, uniq } from 'lodash-es';
import type { CoreTypes } from '../../../core';
import { getDayIndex, getVertexId } from '../../../core/util';
import type { BaseOptions } from '../../../common/types';
import { makeChainable } from '../../../common/make-chainable';


type Undervisningsgrupp = {
  name?:        string;
  socialnumber: string;
  externalid:   string;
  classtype:    1 | 0;
};

function createUndervisningsgrupp (
  collection:      'courses' | 'events',
  entity:          CoreTypes.Mixed.Course | CoreTypes.Mixed.Event,
  fallbackName:    string | undefined,
  did:             string,
  groupPersonsMap: Map<string, CoreTypes.Mixed.Person[]>,
  personsMap:      Map<string, CoreTypes.Mixed.Person>,
  options:         BaseOptions
): Undervisningsgrupp {
  const SSNs = makeChainable(entity.groups ?? [])
    .chain(
      x => x
        .map(x => {
          const include = groupPersonsMap.get(getVertexId(x.to, options))?.map(x => x.SSN?.value)
            .filter((x): x is string => !!x) ?? [];
          const exclude = x.exclude?.map(x => personsMap.get(getVertexId(x, options))?.SSN?.value).filter((x): x is string => !!x) ?? [];
          return include.filter(x => !exclude.includes(x));
        })
        .concat(entity.participants?.map(({ to }) => personsMap.get(getVertexId(to, options))?.SSN?.value).filter((x): x is string => !!x) ?? [])
        .flat(),
      x => uniq(x).join(', ')
    )
    .value;

  return {
    name:         entity.displayName ?? fallbackName,
    socialnumber: SSNs,
    externalid:   `${did}.${getVertexId(entity, options)}${collection == 'courses' ? 'C' : 'E'}`,
    classtype:    0
  };
};

export default (
  schedule: CoreTypes.Mixed.Schedule & { division?: CoreTypes.Mixed.Division },
  options:  BaseOptions = {}
): XLSX.WorkBook => {
  const division = schedule.division ?? schedule.divisions;

  // must have a division id
  const did = getVertexId(division, options);

  // a map of period id -> period
  const periodsMap = new Map(schedule.periods.map(p => [getVertexId(p, options) as string | undefined, p as Pick<CoreTypes.Mixed.Period, 'ranges'>]));
  periodsMap.set(undefined, { ranges: [{ start: division.start, end: division.end }] });

  // a map of group id -> persons
  const groupPersonsMap = new Map<string, CoreTypes.Mixed.Person[]>();
  schedule.persons.forEach(p => {
    if (!p.group) return;
    const gid = getVertexId(p.group, options);
    if (!groupPersonsMap.has(gid)) groupPersonsMap.set(gid, []);
    groupPersonsMap.get(gid)?.push(p);
  });

  // a map of person id -> person
  const personsMap = new Map(schedule.persons.map(p => [getVertexId(p, options), p]));

  // a map of teacher id -> teacher
  const teachersMap = new Map(schedule.teachers.map(t => [getVertexId(t, options), t]));

  // a map of course id -> course
  const coursesMap = new Map(schedule.courses.map(c => [getVertexId(c, options), c]));


  ////
  //// kurser, grupper -> undervisningsgrupper
  ////
  let undervisningsgrupper = new Array<Undervisningsgrupp>();
  schedule.courses.forEach(c => {
    undervisningsgrupper.push(createUndervisningsgrupp('courses', c, '', did, groupPersonsMap, personsMap, options));
  });
  schedule.groups.forEach(g => {
    const SSNs = (groupPersonsMap.get(getVertexId(g, options))?? [])
      .map(x => x.SSN?.value)
      .filter((x): x is string => !!x)
      .join(', ');

    undervisningsgrupper.push({
      name:         g.displayName,
      socialnumber: SSNs,
      externalid:   `${did}.${getVertexId(g, options)}G`,
      classtype:    1
    });
  });

  // for convenience, sort first by classtype in descending order, then by name
  undervisningsgrupper = orderBy(undervisningsgrupper, ['classtype', 'name'], ['desc', 'asc']);


  ////
  //// salar
  ////
  const salar = schedule.locations
    .map(l => ({
      name:       l.displayName,
      externalid: `${did}.${getVertexId(l, options)}`
    }));


  ////
  //// lektioner
  ////
  const lektioner = schedule.events
    .map(e => {
    // must have start and end
      if (!e.start || !e.end) return null;
      const start = moment.utc(e.start);
      const end   = moment.utc(e.end);

      // must have course
      const course = e.course ? coursesMap.get(getVertexId(e.course, options)) : undefined;
      if (!course) return null;
      let groupexternalid = `${did}.${getVertexId(course, options)}C`;

      // if the event overrides the course's groups or participants, create a new overlap group from the event
      if (e.groups || e.participants) {
        const UG = createUndervisningsgrupp('events', e, course.displayName, did, groupPersonsMap, personsMap, options);
        undervisningsgrupper.push(UG);
        groupexternalid = UG.externalid;
      }

      const subject = course.subject;
      const _period  = e.period ?? course.period ?? schedule.settings.period;
      const period = periodsMap.get(_period ? getVertexId(_period, options) : undefined);

      const weeks = period?.ranges.map(x => {
      // fetch all weeks between start and end
        const start = moment.utc(x.start);
        const end   = moment.utc(x.end);
        const weeks = new Array<number>();
        while (start.isBefore(end)) {
          weeks.push(start.week());
          start.add(1, 'week');
        }
        return weeks;
      }).flat()
        .join(', ');

      const teachers = (e.teachers ?? course.teachers ?? [])
        .map(x => teachersMap.get(getVertexId(x.to, options))?.ids)
        .filter((x): x is string => !!x)
        .join(', ');

      const rooms = (e.inLocations as CoreTypes.Deep.Location[] ?? [])
        .filter(Boolean)
        .map(x => `${did}.${getVertexId(x, options)}`)
        .join(', ');

      return {
        dayid:           getDayIndex(start),
        starttime:       start.format('HHmm'),
        length:          end.diff(start, 'minutes'),
        roomexternalid:  rooms,
        subject:         subject,
        exweek:          '',
        inweek:          weeks,
        period:          '',
        groupexternalid: groupexternalid,
        teacherid:       teachers,
        externalref:     `${did}.${getVertexId(e, options)}`,
      };
    })
    .filter(x => x != null);


  ////
  //// store in workbook
  ////
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(undervisningsgrupper), '1. Undervisningsgrupper');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(salar),                '2. Salar');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(lektioner),            '3. Lektioner');

  return wb;
};
